import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { message } from '@client/components/_ui/feedback/message';
import { Space } from '@client/components/_ui/layout/Space';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { createFileRoute } from '@tanstack/react-router';
import { Card, Typography } from 'antd';
import { Flag, RefreshCw } from 'lucide-react';

const { Paragraph, Text } = Typography;

export const Route = createFileRoute('/admin-console/tools/feature-flags')({
    staticData: {
        permissions: ['admin_system:full'],
        ui: {
            title: 'Feature flags',
            menuDescription: 'PostHog feature flag controls',
        },
        breadcrumb: { title: 'Feature flags' },
    },
    component: FeatureFlagsPage,
});

function FeatureFlagsPage() {
    const { trpc } = useTrpc();

    const reloadMutation = trpc.admin.posthog.reloadFlags.useMutation({
        onSuccess: (result) => {
            if (result.success) {
                message.success('Feature flag definitions reloaded');
            } else {
                message.warning(
                    'Reload skipped because the PostHog client is not initialized on this server instance.',
                );
            }
        },
        onError: (error) => {
            console.error('[Admin feature flags] Failed to reload feature flags', error);
            message.error('Failed to reload feature flags. Please try again.');
        },
    });

    return (
        <PageController route={Route}>
            <div className="mx-auto max-w-3xl p-6">
                <Card
                    title={
                        <Space>
                            <Flag size={16} />
                            <span>PostHog feature flags</span>
                        </Space>
                    }
                >
                    <Paragraph>
                        Refresh the PostHog feature flag definitions on the server instance that handles this request.
                        Normally the server polls PostHog every 30 seconds (10 seconds in non-production), so a flag
                        change usually reaches a given instance within that window. Use this button to shorten that wait
                        on the responding instance during investigation or incident response.
                    </Paragraph>
                    <Paragraph>
                        <Text type="secondary" className="text-xs">
                            Note: PostHog does not support flag-change webhooks. This button reloads the local SDK
                            definitions and clears the Redis-backed SWR caches used by server-side flag reads. Each
                            server instance still polls independently, so in a multi-instance deployment this is not a
                            cluster-wide guarantee. Another worker can continue serving older definitions until its next
                            poll. For sub-30 s propagation of a specific flag across all workers, switch that flag to
                            remote evaluation instead.
                        </Text>
                    </Paragraph>
                    <Button
                        type="primary"
                        icon={<RefreshCw size={16} />}
                        loading={reloadMutation.isPending}
                        onClick={() => reloadMutation.mutate()}
                    >
                        Reload feature flags
                    </Button>
                </Card>
            </div>
        </PageController>
    );
}
